import { useState, useCallback, useMemo } from 'react';
import { UseOpenConfirmLogoutModalType } from './interfaces';
import { sendCustomSentryBreadcrumb } from '@helpers/sentry';
import { AnalyticsType } from '@interfaces/analytics';
import { useCommunications } from '@core/communications/providers/CommunicationsProvider';
import { useMytaverse } from '@providers/MytaverseProvider';

export const useOpenConfirmLogoutModal: UseOpenConfirmLogoutModalType = () => {
  const [open, setOpen] = useState(false);

  const openModal = useCallback(() => setOpen(true), []);
  const closeModal = useCallback(() => setOpen(false), []);

  return useMemo(
    () => ({ open, openModal, closeModal }),
    [open, openModal, closeModal],
  );
};

export const useLogout = ({
  onConfirm,
  onClose,
}: {
  onClose?: () => void;
  onConfirm?: () => void;
}) => {
  const {
    stopShareCameraToScreen,
    stopScreenSharing,
    isSharingToScreen,
    isSharingScreen,
  } = useCommunications();

  const { logoutUser, trackAnalytics } = useMytaverse();

  return useCallback(async () => {
    if (isSharingScreen) {
      await stopScreenSharing();
    }
    if (isSharingToScreen) {
      await stopShareCameraToScreen();
    }

    sendCustomSentryBreadcrumb({ message: 'log out from event' });
    await trackAnalytics(AnalyticsType.LogoutClick);
    logoutUser();
    onConfirm && onConfirm();
    onClose && onClose();
  }, [
    trackAnalytics,
    logoutUser,
    isSharingScreen,
    isSharingToScreen,
    stopScreenSharing,
    stopShareCameraToScreen,
    onConfirm,
    onClose,
  ]);
};
